import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { differenceInHours } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { useMemo } from "react";
import Header from "../Header";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "../ui/button";

export default function PatientDashboard() {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const { email: storedEmail } = storedUser;

  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [editData, setEditData] = useState({ date: "", time: "" });
  const [rescheduleSuccess, setRescheduleSuccess] = useState(false);

  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [message, setMessage] = useState("");

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  const [searchBookingId, setSearchBookingId] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [filter, setFilter] = useState("today");
  const [consultationFilter, setConsultationFilter] = useState("All");
  const [filterDate, setFilterDate] = useState("");

  const [cancelError, setCancelError] = useState("");
  const [cancelReason, setCancelReason] = useState("");

  const slots = useMemo(
    () => [
      "09:30 AM",
      "10:00 AM",
      "10:30 AM",
      "11:00 AM",
      "11:30 AM",
      "12:00 PM",
      "12:30 PM",
      "01:00 PM",
      "04:30 PM",
      "05:00 PM",
      "05:30 PM",
      "06:00 PM",
      "06:30 PM",
      "07:00 PM",
      "07:30 PM",
      "08:00 PM",
      "08:30 PM",
      "09:00 PM",
    ],
    []
  );

  const today = new Date();

  // Helper: build valid Date object from date+time strings
  const buildAppointmentDateTime = (dateStr, timeStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const [time, modifier] = timeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    if (modifier === "PM" && hours < 12) hours += 12;
    if (modifier === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes);
  };

  // Fetch appointments live
  useEffect(() => {
    if (!storedEmail) return;

    const q = query(
      collection(db, "appointments"),
      where("email", "==", storedEmail)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const appointmentsData = snapshot.docs.map((doc) => {
        const data = doc.data();

        // Convert Python datetime (from backend) to JS Date if needed
        let createdAt = null;
        if (data.timestamp) {
          if (data.timestamp.seconds !== undefined) {
            // Firestore Timestamp
            createdAt = new Date(data.timestamp.seconds * 1000);
          } else {
            // ISO string from backend
            createdAt = new Date(data.timestamp);
          }
        }

        return {
          id: doc.id,
          bookingId: data.bookingId || doc.id,
          ...data,
          date: data.date || "N/A",
          time: data.time || "N/A",
          service: data.service || "N/A",
          message: data.message || "N/A",
          consultationType: data.consultationType || "Walk-in",
          createdAt, // JS Date object
        };
      });

      appointmentsData.sort(
        (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)
      );

      setAppointments(appointmentsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [storedEmail]);

  // Cancel appointment
  const handleCancel = async () => {
    if (!currentAppointment) return;

    if (!cancelReason.trim()) {
      setCancelError("Please provide a reason for cancellation.");
      return;
    }

    try {
      const appointmentRef = doc(db, "appointments", currentAppointment.id);
      await updateDoc(appointmentRef, {
        status: "Canceled",
        cancelReason: cancelReason.trim(),
      });
      setCancelSuccess(true);
      setCancelReason("");
      setCancelError("");
    } catch (err) {
      console.error(err);
    }
  };

  // Reschedule appointment
  const handleEditSubmit = async () => {
    if (!editData.date || !editData.time) return alert("Fill date and time.");
    try {
      const appointmentRef = doc(db, "appointments", currentAppointment.id);
      await updateDoc(appointmentRef, {
        date: editData.date,
        time: editData.time,
        status: "Pending",
      });
      setRescheduleSuccess(true);
    } catch (err) {
      console.error(err);
    }
  };

  // Filtered appointments
  const filteredAppointments = appointments.filter((appt) => {
    const apptDate = new Date(appt.date);
    let passesDate = true;

    if (filter === "today") {
      passesDate =
        apptDate.getFullYear() === today.getFullYear() &&
        apptDate.getMonth() === today.getMonth() &&
        apptDate.getDate() === today.getDate();
    } else if (filter === "week") {
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 7);
      passesDate = apptDate >= weekAgo && apptDate <= today;
    } else if (filter === "month") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      passesDate = apptDate >= start && apptDate <= today;
    } else if (filter === "6months") {
      const start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      passesDate = apptDate >= start && apptDate <= today;
    }

    let passesConsultation =
      consultationFilter === "All" ||
      appt.consultationType === consultationFilter;

    const apptDateFil = new Date(appt.date);
    const formattedDate = isNaN(apptDateFil.getTime())
      ? ""
      : apptDateFil.toISOString().split("T")[0];
    const dateMatch = filterDate === "" || formattedDate === filterDate;

    const bookingIdMatch =
      searchBookingId === "" ||
      (appt.bookingId || appt.id)
        .toLowerCase()
        .includes(searchBookingId.toLowerCase());

    return passesDate && passesConsultation && dateMatch && bookingIdMatch;
  });

  // Columns
  const getColumns = () => {
    switch (consultationFilter) {
      case "Walk-in":
        return [
          "Booking ID",
          // "Booked On",
          "Appointment Date",
          "Time",
          "Patient",
          "Email",
          "Phone",
          "Service",
          "Consultation Type",
          "Branch",
          "Message",
          "Status",
          "Actions",
        ];
      case "Home Visit":
        return [
          "Booking ID",
          // "Booked On",
          "Appointment Date",
          "Time",
          "Patient",
          "Consultation Type",
          "Email",
          "Phone",
          "Service",
          "Pincode",
          "Locality",
          "FlatNo",
          "Landmark",
          "Message",
          "Status",
          "Actions",
        ];
      case "Virtual Consultation":
        return [
          "Booking ID",
          // "Booked On",
          "Appointment Date",
          "Time",
          "Patient",
          "Email",
          "Phone",
          "Service",
          "Consultation Type",
          "Message",
          "Status",
          "Actions",
        ];
      default:
        return [
          "Booking ID",
          // "Booked On",
          "Appointment Date",
          "Time",
          "Patient",
          "Consultation Type",
          "Service",
          "Status",
          "Actions",
        ];
    }
  };
  const columns = getColumns();

  // Stats
  const stats = [
    {
      label: "Upcoming",
      color: "bg-blue-100 text-blue-800",
      count: filteredAppointments.filter(
        (a) => new Date(a.date) >= today && a.status !== "Canceled"
      ).length,
    },
    {
      label: "Confirmed",
      color: "bg-green-100 text-green-800",
      count: filteredAppointments.filter((a) => a.status === "Confirmed")
        .length,
    },
    {
      label: "Pending",
      color: "bg-gray-100 text-gray-800",
      count: filteredAppointments.filter((a) => a.status === "Pending").length,
    },
    {
      label: "Canceled",
      color: "bg-red-100 text-red-800",
      count: filteredAppointments.filter((a) => a.status === "Canceled").length,
    },
    {
      label: "Rescheduled",
      color: "bg-yellow-100 text-yellow-800",
      count: filteredAppointments.filter((a) => a.status === "Rescheduled")
        .length,
    },
  ];

  return (
    <>
      <Header hideBookButton={true} />
      <div className="min-h-screen bg-gray-50 font-sans px-4 sm:px-6 lg:px-8 pt-24">
        {/* Header + Filters */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#204080]">
              Your Journey with Us
            </h2>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              View, manage, and reschedule your care appointments anytime.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Search by Booking ID"
              value={searchBookingId}
              onChange={(e) => setSearchBookingId(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border"
            >
              <option value="today">Today</option>
              <option value="week">Past Week</option>
              <option value="month">Past Month</option>
              <option value="6months">Last 6 Months</option>
              <option value="all">All</option>
            </select>
            <select
              value={consultationFilter}
              onChange={(e) => setConsultationFilter(e.target.value)}
              className="px-3 py-2 rounded-lg border"
            >
              <option value="All">All Types</option>
              <option value="Walk-in">Walk-in</option>
              <option value="Home Visit">Home Visit</option>
              <option value="Virtual Consultation">Virtual Consultation</option>
            </select>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={`flex flex-col items-center justify-center py-4 rounded-xl ${stat.color} shadow-sm`}
            >
              <p className="text-xs sm:text-sm font-medium">{stat.label}</p>
              <p className="text-lg sm:text-2xl font-bold mt-1">{stat.count}</p>
            </div>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg shadow-lg bg-white border border-gray-200">
          {loading ? (
            <p className="text-center p-8 text-gray-600 text-lg font-medium">
              Loading your appointments...
            </p>
          ) : filteredAppointments.length === 0 ? (
            <p className="text-center p-8 text-gray-500 italic">
              No appointments found.
            </p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm table-auto">
              <thead className="bg-gradient-to-br from-blue-50 to-slate-50">
                <tr>
                  {columns.map((col, i) => (
                    <th
                      key={i}
                      className="px-4 sm:px-6 py-3 text-left font-bold text-[#204080] uppercase text-xs sm:text-sm whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredAppointments.map((appt, idx) => (
                  <tr
                    key={appt.id}
                    className={idx % 2 === 0 ? "bg-slate-50" : "bg-white"}
                  >
                    {columns.map((col, i) => {
                      switch (col) {
                        case "Booking ID":
                          return (
                            <td key={i} className="px-4 py-3 ">
                              {appt.bookingId || "-"}
                            </td>
                          );
                        // case "Booked On":
                        //   return (
                        //     <td
                        //       key={i}
                        //       className="px-4 py-3  whitespace-nowrap "
                        //     >
                        //       {appt.createdAt?.toDate
                        //         ? appt.createdAt.toDate().toLocaleString()
                        //         : "-"}
                        //     </td>
                        //   );
                        case "Appointment Date":
                          return (
                            <td key={i} className="px-4 py-3">
                              {appt.date}
                            </td>
                          );
                        case "Time":
                          return (
                            <td
                              key={i}
                              className="px-4 py-3  whitespace-nowrap "
                            >
                              {appt.time}
                            </td>
                          );
                        case "Patient":
                          return (
                            <td className="px-4 py-3 whitespace-nowrap">
                              {appt.name || "-"}
                            </td>
                          );
                        case "Email":
                          return (
                            <td key={i} className="px-4 py-3">
                              {appt.email}
                            </td>
                          );
                        case "Phone":
                          return (
                            <td key={i} className="px-4 py-3">
                              {appt.phone}
                            </td>
                          );
                        case "Service":
                          return (
                            <td key={i} className="px-4 py-3">
                              {appt.service}
                            </td>
                          );
                        case "Consultation Type":
                          return (
                            <td key={i} className="px-4 py-3">
                              {appt.consultationType}
                            </td>
                          );
                        case "Branch":
                          return (
                            <td
                              key={i}
                              className="px-4 py-3  whitespace-nowrap "
                            >
                              {appt.branch}
                            </td>
                          );
                        case "Message":
                          return (
                            <td key={i} className="px-4 py-3">
                              {appt.message}
                            </td>
                          );
                        case "Status":
                          return (
                            <td key={i} className="px-4 py-3">
                              <span
                                className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                  appt.status === "Confirmed"
                                    ? "bg-green-100 text-green-800"
                                    : appt.status === "Pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : appt.status === "Canceled"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {appt.status}
                              </span>
                            </td>
                          );
                        case "Actions":
                          return (
                            <td key={i} className="px-4 py-3 flex gap-2">
                              {/* Reschedule */}
                              <button
                                onClick={() => {
                                  const apptDateTime = buildAppointmentDateTime(
                                    appt.date,
                                    appt.time
                                  );
                                  const hoursDiff = differenceInHours(
                                    apptDateTime,
                                    new Date()
                                  );

                                  if (hoursDiff < 24) {
                                    setConfirmAction({
                                      type: "reschedule",
                                      appointment: appt,
                                    });
                                    setMessage(
                                      "Sorry, rescheduling is only allowed at least 24 hours before your appointment."
                                    );
                                    setIsMessageOpen(true);
                                  } else {
                                    setCurrentAppointment(appt);
                                    setEditData({
                                      date: appt.date,
                                      time: appt.time,
                                    });
                                    setIsEditModalOpen(true);
                                  }
                                }}
                                disabled={appt.status === "Canceled"}
                                className={`px-3 py-1 text-xs rounded ${
                                  appt.status === "Canceled"
                                    ? "bg-gray-300 text-gray-500"
                                    : "bg-yellow-500 text-white hover:bg-yellow-600"
                                }`}
                              >
                                Reschedule
                              </button>

                              {/* Cancel */}
                              <button
                                onClick={() => {
                                  const apptDateTime = buildAppointmentDateTime(
                                    appt.date,
                                    appt.time
                                  );
                                  const hoursDiff = differenceInHours(
                                    apptDateTime,
                                    new Date()
                                  );

                                  if (hoursDiff < 24) {
                                    setConfirmAction({
                                      type: "cancel",
                                      appointment: appt,
                                    });
                                    setMessage(
                                      "Oops! Changes and cancellations must be made at least 24 hours before your appointment."
                                    );
                                    setIsMessageOpen(true);
                                  } else {
                                    setCurrentAppointment(appt);
                                    setIsCancelModalOpen(true);
                                  }
                                }}
                                disabled={appt.status === "Canceled"}
                                className={`px-3 py-1 text-xs rounded ${
                                  appt.status === "Canceled"
                                    ? "bg-gray-300 text-gray-500"
                                    : "bg-red-500 text-white hover:bg-red-600"
                                }`}
                              >
                                Cancel
                              </button>
                            </td>
                          );

                        default:
                          return <td key={i}></td>;
                      }
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Info message dialog */}
        <Dialog open={isMessageOpen} onOpenChange={setIsMessageOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-[#204080]">
                {confirmAction?.type === "cancel"
                  ? "Cancel Appointment"
                  : "Reschedule"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-center py-4">{message}</p>
            <DialogFooter>
              <Button
                onClick={() => setIsMessageOpen(false)}
                className="bg-[#204080] text-white"
              >
                OK
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reschedule dialog */}
        <Dialog
          open={isEditModalOpen}
          onOpenChange={(open) => {
            setIsEditModalOpen(open);
            if (!open) setRescheduleSuccess(false);
          }}
        >
          <DialogContent>
            {!rescheduleSuccess ? (
              <>
                <DialogHeader>
                  <DialogTitle className="text-[#204080]">
                    Reschedule Appointment
                  </DialogTitle>
                  <DialogDescription>
                    Choose new date & time for rescheduling.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-4 py-4">
                  {/* Date Picker */}
                  <DatePicker
                    selected={editData.date ? new Date(editData.date) : null}
                    onChange={(date) =>
                      setEditData({
                        ...editData,
                        date: date ? format(date, "yyyy-MM-dd") : "",
                      })
                    }
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()} // can't pick past dates
                    placeholderText="Select a Date"
                    wrapperClassName="w-full"
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
                  />

                  {/* Time slots */}
                  <select
                    value={editData.time}
                    onChange={(e) =>
                      setEditData({ ...editData, time: e.target.value })
                    }
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
                  >
                    <option value="">Select a Time</option>
                    {slots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditModalOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleEditSubmit}
                    className="bg-[#204080] text-white"
                  >
                    Confirm
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Rescheduled Successfully</DialogTitle>
                </DialogHeader>
                <p className="text-center py-6 text-green-700 font-medium">
                  Reschedule confirmation has been sent to your registered email
                  address.
                </p>
                <DialogFooter>
                  <Button onClick={() => setIsEditModalOpen(false)}>OK</Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel dialog */}
        <Dialog
          open={isCancelModalOpen}
          onOpenChange={(open) => {
            setIsCancelModalOpen(open);
            if (!open) setCancelSuccess(false);
          }}
        >
          <DialogContent>
            {!cancelSuccess ? (
              <>
                <DialogHeader>
                  <DialogTitle className="text-[#204080]">
                    Cancel Appointment
                  </DialogTitle>
                </DialogHeader>
                <p className="py-2">
                  Are you sure you want to cancel this appointment?
                </p>

                <div className="flex flex-col gap-2 py-2">
                  <label className="text-sm font-medium">
                    Reason for cancellation{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => {
                      setCancelReason(e.target.value);
                      if (e.target.value.trim()) setCancelError(""); // clear error on input
                    }}
                    placeholder="Enter your reason"
                    className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#204080]"
                    rows={3}
                    required
                  />
                  {cancelError && (
                    <p className="text-red-600 text-sm mt-1">{cancelError}</p>
                  )}
                </div>

                <DialogFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsCancelModalOpen(false);
                      setCancelReason("");
                    }}
                  >
                    No
                  </Button>
                  <Button
                    onClick={handleCancel}
                    className="bg-red-600 text-white"
                  >
                    Yes, Cancel
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Canceled Successfully</DialogTitle>
                </DialogHeader>
                <p className="text-center py-6 text-red-700 font-medium">
                  We will contact you at your registered email address.
                </p>
                <DialogFooter>
                  <Button
                    onClick={() => setIsCancelModalOpen(false)}
                    className="bg-[#204080] text-white"
                  >
                    OK
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
